// @oasis-permission-inherits: /audio (mirage-audio:view)
import { Icon } from "@mirage-web/icons";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { ActionPill, Pill } from "~/modules/mirage/components/pill";
import {
  fetchMirageAudioQuery,
  lookupMirageAudioByIdQuery,
  queryVoiceIdMetadataQuery,
} from "~/modules/mirage/queries";
import { formatRelativeTime } from "~/modules/videos/utils";

export const Route = createFileRoute("/_dash/audio/")({
  validateSearch: z.object({
    type: z.enum(["user", "org"]).optional(),
    id: z.string().optional(),
    audioId: z.string().optional(),
    tags: z.array(z.string()).optional(),
    userId: z.string().optional(),
  }),
  component: RouteComponent,
});

interface AudioData {
  id: string;
  completedAt: number;
  url: string;
  referenceUrl?: string | null;
  text: string;
  tags?: string[] | null;
  ttsVoiceId?: string | null;
}

function VoiceMetadataPill({ voiceId }: { voiceId: string }) {
  const { data: voiceMetadata, isLoading } = queryVoiceIdMetadataQuery.useQuery({
    pathParams: { voiceId },
  });

  if (isLoading || !voiceMetadata) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>
          <Pill label="Voice" value={voiceMetadata.name ?? voiceId} />
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1 text-xs">
          <div>
            <span className="font-medium">Name:</span> {voiceMetadata.name}
          </div>
          <div>
            <span className="font-medium">Provider:</span> {voiceMetadata.providerId}
          </div>
          <div>
            <span className="font-medium">Gender:</span> {voiceMetadata.gender}
          </div>
          <div>
            <span className="font-medium">Accent:</span> {voiceMetadata.accent}
          </div>
          <div>
            <span className="font-medium">ID:</span> {voiceId}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function VoiceMetadataDisplay({ voiceId }: { voiceId: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: voiceMetadata, isLoading } = queryVoiceIdMetadataQuery.useQuery({
    pathParams: { voiceId },
  });

  if (isLoading || !voiceMetadata) {
    return null;
  }

  return (
    <div className="text-xs text-muted-foreground">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className="hover:text-foreground transition-colors flex items-center gap-1"
      >
        <Icon name={isExpanded ? "chevron-down" : "chevron-right"} className="h-3 w-3" />
        <span className="font-medium">Voice:</span>
        <span>{voiceMetadata.name}</span>
      </button>
      {isExpanded && (
        <div className="mt-1 ml-4 space-y-0.5">
          <div>
            <span className="font-medium">Provider:</span> {voiceMetadata.providerId}
          </div>
          <div>
            <span className="font-medium">Gender:</span> {voiceMetadata.gender}
          </div>
          <div>
            <span className="font-medium">Accent:</span> {voiceMetadata.accent}
          </div>
          <div>
            <span className="font-medium">ID:</span> {voiceId}
          </div>
        </div>
      )}
    </div>
  );
}

interface AudioFeedCardProps {
  audio: AudioData;
  isSelected: boolean;
  onClick: () => void;
}

function AudioFeedCard({ audio, isSelected, onClick }: AudioFeedCardProps) {
  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName !== "AUDIO" && !target.closest("audio")) {
      onClick();
    }
  };

  const environmentTags = ["prod", "dev", "beta", "shadow"];

  const getTagColor = (tag: string) => {
    if (environmentTags.includes(tag.toLowerCase())) {
      return "bg-green-500/20 text-green-700 border-green-500/30";
    }
    const tagColors = [
      "bg-blue-500/20 text-blue-700 border-blue-500/30",
      "bg-purple-500/20 text-purple-700 border-purple-500/30",
      "bg-pink-500/20 text-pink-700 border-pink-500/30",
      "bg-cyan-500/20 text-cyan-700 border-cyan-500/30",
      "bg-amber-500/20 text-amber-700 border-amber-500/30",
      "bg-teal-500/20 text-teal-700 border-teal-500/30",
    ];
    const hash = tag.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return tagColors[hash % tagColors.length];
  };

  return (
    <Card
      className={`p-4 cursor-pointer transition-colors hover:bg-accent/50 ${
        isSelected ? "ring-2 ring-primary" : ""
      }`}
      onClick={handleCardClick}
    >
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm line-clamp-2 flex-1">{audio.text}</p>
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            {formatRelativeTime(audio.completedAt)}
          </div>
        </div>
        {audio.tags && audio.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {[...audio.tags]
              .sort((a, b) => {
                const aIsEnv = environmentTags.includes(a.toLowerCase());
                const bIsEnv = environmentTags.includes(b.toLowerCase());
                if (aIsEnv && !bIsEnv) {
                  return -1;
                }
                if (!aIsEnv && bIsEnv) {
                  return 1;
                }
                return 0;
              })
              .map((tag, index) => (
                <span
                  key={`${tag}-${index}`}
                  className={`px-2 py-0.5 text-xs rounded-full border font-medium ${getTagColor(tag)}`}
                >
                  {tag}
                </span>
              ))}
          </div>
        )}
        {audio.ttsVoiceId && <VoiceMetadataDisplay voiceId={audio.ttsVoiceId} />}
        <audio
          src={audio.url}
          controls
          className="w-full"
          onClick={(e) => e.stopPropagation()}
          aria-label="Output audio player"
        />
      </div>
    </Card>
  );
}

interface WordTimestamp {
  text: string;
  startTime: number;
  endTime: number;
}

function CopyableText({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      {label && <h4 className="text-sm font-medium text-muted-foreground mb-2">{label}</h4>}
      <div className="relative group">
        <p className="text-sm bg-muted p-3 rounded pr-10">{text}</p>
        <Button
          size="icon"
          variant="ghost"
          className="absolute bottom-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={handleCopy}
        >
          <Icon name={copied ? "check" : "copy"} className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function ExpandableCopyableText({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shouldTruncate = text.length > 150;

  return (
    <div className="relative">
      {label && <h4 className="text-xs font-medium text-muted-foreground mb-2">{label}</h4>}
      <div className="relative group">
        <p
          className={`text-xs bg-muted p-2.5 rounded pr-10 ${!expanded && shouldTruncate ? "line-clamp-3" : ""}`}
        >
          {text}
        </p>
        <div className="absolute bottom-1.5 right-1.5 flex gap-1">
          {shouldTruncate && (
            <Button
              size="sm"
              variant="ghost"
              className="h-5 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "Show less" : "Show more"}
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleCopy}
          >
            <Icon name={copied ? "check" : "copy"} className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function WordTimestampsTimeline({ timestamps }: { timestamps: WordTimestamp[] }) {
  const formatTime = (seconds: number) => {
    return `${seconds.toFixed(2)}s`;
  };

  if (!timestamps || timestamps.length === 0) {
    return <div className="text-sm text-muted-foreground">No timestamps available</div>;
  }

  const totalDuration = timestamps[timestamps.length - 1]?.endTime;

  if (!totalDuration || totalDuration <= 0) {
    return <div className="text-sm text-muted-foreground">Invalid timestamp data</div>;
  }

  const durations = timestamps.map((t) => t.endTime - t.startTime);
  const maxDuration = Math.max(...durations);
  const minDuration = Math.min(...durations);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>0s</span>
        <div className="flex-1" />
        <span>{formatTime(totalDuration)}</span>
      </div>
      <div className="relative h-8 bg-muted rounded overflow-hidden">
        {timestamps.map((timestamp, index) => {
          const startPercent = (timestamp.startTime / totalDuration) * 100;
          const widthPercent = ((timestamp.endTime - timestamp.startTime) / totalDuration) * 100;
          const duration = timestamp.endTime - timestamp.startTime;
          const normalizedDuration =
            maxDuration === minDuration
              ? 0.5
              : (duration - minDuration) / (maxDuration - minDuration);
          const lightness = 90 - normalizedDuration * 60;

          return (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <div
                  className="absolute h-full border-r border-background hover:opacity-80 transition-opacity cursor-pointer"
                  style={{
                    left: `${startPercent}%`,
                    width: `${widthPercent}%`,
                    backgroundColor: `hsl(220, 70%, ${lightness}%)`,
                  }}
                />
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs space-y-1">
                  <div className="font-semibold">{timestamp.text}</div>
                  <div className="text-muted-foreground">
                    {formatTime(timestamp.startTime)} - {formatTime(timestamp.endTime)}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

interface ComputationStage {
  stage_name?: string;
  stage_latency_ms?: number;
  stage_result?: string;
  input?: object;
  output?: object;
}

interface AudioDetailsPanelProps {
  audioId: string;
  onClose: () => void;
}

function AudioDetailsPanel({ audioId, onClose }: AudioDetailsPanelProps) {
  const { data: audioDetails, isLoading } = lookupMirageAudioByIdQuery.useQuery({
    pathParams: { audioId },
  });
  const [yamlCopied, setYamlCopied] = useState(false);
  const [requestCopied, setRequestCopied] = useState(false);

  return (
    <div className="fixed right-0 top-0 h-full w-[700px] bg-background border-l shadow-xl z-50 overflow-y-auto">
      <div className="sticky top-0 bg-background border-b p-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Audio Details</h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <Icon name="cross-small" className="h-5 w-5" />
        </Button>
      </div>

      <div className="p-4 space-y-6">
        {isLoading && (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {audioDetails && (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <ActionPill
                label="Inference ID"
                actions={[
                  {
                    label: audioId,
                    type: "copy",
                    value: audioId,
                  },
                ]}
              />
              {audioDetails.completedAt && audioDetails.createdAt && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Pill
                        label="Inference Duration"
                        value={`${((audioDetails.completedAt - audioDetails.createdAt) / 1000).toFixed(2)}s`}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1 text-xs">
                      <div>
                        <span className="font-medium">Created: </span>
                        {new Date(audioDetails.createdAt).toLocaleString("en-US", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </div>
                      <div>
                        <span className="font-medium">Completed: </span>
                        {new Date(audioDetails.completedAt).toLocaleString("en-US", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
              {audioDetails.seed && (
                <ActionPill
                  label="Seed"
                  actions={[
                    {
                      label: audioDetails.seed,
                      type: "copy",
                      value: audioDetails.seed,
                    },
                  ]}
                />
              )}
              {audioDetails.ttsVoiceId && <VoiceMetadataPill voiceId={audioDetails.ttsVoiceId} />}
            </div>

            {audioDetails.audio?.url && (
              <div className="space-y-4">
                <h3 className="text-base font-semibold">Output</h3>
                {audioDetails.text && <CopyableText text={audioDetails.text} />}
                <audio
                  src={audioDetails.audio.url}
                  controls
                  className="w-full"
                  aria-label="Output audio player"
                />
                {audioDetails.audio?.shadowUrl && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">
                      Elevenlabs Shadow
                    </h4>
                    <audio
                      src={audioDetails.audio.shadowUrl}
                      controls
                      className="w-full"
                      aria-label="Shadow audio player"
                    />
                  </div>
                )}
              </div>
            )}

            {audioDetails.audioEvalSummary && (
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="evaluation" className="bg-muted/50 rounded-md px-3 border-0">
                  <AccordionTrigger className="text-base font-semibold py-3">
                    Audio Evaluation
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pt-2">
                      {audioDetails.audioEvalSummary.speechIntelligibility != null && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Speech Intelligibility</span>
                            <span className="font-medium">
                              {audioDetails.audioEvalSummary.speechIntelligibility.toFixed(1)} / 10
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{
                                width: `${(audioDetails.audioEvalSummary.speechIntelligibility / 10) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {audioDetails.audioEvalSummary.naturalness != null && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Naturalness</span>
                            <span className="font-medium">
                              {audioDetails.audioEvalSummary.naturalness.toFixed(1)} / 10
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{
                                width: `${(audioDetails.audioEvalSummary.naturalness / 10) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {audioDetails.audioEvalSummary.expressiveness != null && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Expressiveness</span>
                            <span className="font-medium">
                              {audioDetails.audioEvalSummary.expressiveness.toFixed(1)} / 10
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{
                                width: `${(audioDetails.audioEvalSummary.expressiveness / 10) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {audioDetails.audioEvalSummary.technicalQuality != null && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Technical Quality</span>
                            <span className="font-medium">
                              {audioDetails.audioEvalSummary.technicalQuality.toFixed(1)} / 10
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{
                                width: `${(audioDetails.audioEvalSummary.technicalQuality / 10) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {audioDetails.audioEvalSummary.pacing != null && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Pacing</span>
                            <span className="font-medium">
                              {audioDetails.audioEvalSummary.pacing.toFixed(1)} / 10
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{
                                width: `${(audioDetails.audioEvalSummary.pacing / 10) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {audioDetails.audioEvalSummary.transcriptAlignment != null && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Transcript Alignment</span>
                            <span className="font-medium">
                              {audioDetails.audioEvalSummary.transcriptAlignment.toFixed(1)} / 10
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{
                                width: `${(audioDetails.audioEvalSummary.transcriptAlignment / 10) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {audioDetails.audioEvalSummary.descriptionMatch != null && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Description Match</span>
                            <span className="font-medium">
                              {audioDetails.audioEvalSummary.descriptionMatch.toFixed(1)} / 10
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{
                                width: `${(audioDetails.audioEvalSummary.descriptionMatch / 10) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {audioDetails.audioEvalSummary.referenceMatch != null && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Reference Match</span>
                            <span className="font-medium">
                              {audioDetails.audioEvalSummary.referenceMatch.toFixed(1)} / 10
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{
                                width: `${(audioDetails.audioEvalSummary.referenceMatch / 10) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {audioDetails.audioEvalSummary.explanation && (
                        <div className="pt-2">
                          <ExpandableCopyableText
                            text={audioDetails.audioEvalSummary.explanation}
                            label="Evaluation Explanation"
                          />
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}

            {audioDetails.inferenceResult && (
              <Accordion
                type="single"
                collapsible
                defaultValue="inference-output"
                className="w-full"
              >
                <AccordionItem value="inference-output">
                  <AccordionTrigger className="text-base font-semibold">
                    Inference Result
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-2">
                      {audioDetails.inferenceResult.url && (
                        <audio
                          src={audioDetails.inferenceResult.url}
                          controls
                          className="w-full"
                          aria-label="Inference Result audio player"
                        />
                      )}

                      {audioDetails.inferenceResult.transcript && (
                        <ExpandableCopyableText
                          text={audioDetails.inferenceResult.transcript}
                          label="Output Transcript"
                        />
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}

            {audioDetails.reference?.url && (
              <Accordion type="single" collapsible defaultValue="reference" className="w-full">
                <AccordionItem value="reference">
                  <AccordionTrigger className="text-base font-semibold">Reference</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-2">
                      <audio
                        src={audioDetails.reference.url}
                        controls
                        className="w-full"
                        aria-label="Reference audio player"
                      />

                      {audioDetails.reference.transcript && (
                        <CopyableText
                          text={audioDetails.reference.transcript}
                          label="Scribe Transcription"
                        />
                      )}

                      {audioDetails.reference.wordTimestamps && (
                        <WordTimestampsTimeline
                          timestamps={audioDetails.reference.wordTimestamps}
                        />
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}

            {audioDetails.segments && audioDetails.segments.length > 0 && (
              <Accordion type="single" collapsible className="w-full">
                {audioDetails.segments.map((segment, index) => (
                  <AccordionItem key={index} value={`segment-${index}`}>
                    <AccordionTrigger className="text-sm">Segment {index + 1}</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4">
                        {(segment.startMs != null || segment.endMs != null) && (
                          <div className="flex gap-2">
                            {segment.startMs != null && (
                              <Pill label="Start trim" value={`${segment.startMs}ms`} />
                            )}
                            {segment.endMs != null && (
                              <Pill label="End trim" value={`${segment.endMs}ms`} />
                            )}
                          </div>
                        )}
                        {segment.targetText && (
                          <CopyableText text={segment.targetText} label="Target Text" />
                        )}
                        {segment.transcript && (
                          <CopyableText text={segment.transcript} label="Scribe Transcription" />
                        )}
                        {segment.wordTimestamps && (
                          <WordTimestampsTimeline timestamps={segment.wordTimestamps} />
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}

            {audioDetails.inferenceRequest &&
              (() => {
                const request = audioDetails.inferenceRequest;

                const generateRequestDisplay = () => {
                  const lines: string[] = [];

                  if (request?.targetText) {
                    lines.push(`target_text: "${request.targetText}"`);
                  }

                  if (request?.referenceAudioUri) {
                    lines.push(`reference_audio_uri: "${request.referenceAudioUri}"`);
                  }

                  if (request?.voiceDescription) {
                    lines.push(`voice_description: "${request.voiceDescription}"`);
                  }

                  if (request?.seed) {
                    lines.push(`seed: "${request.seed}"`);
                  }

                  if (request?.modelVersion) {
                    lines.push(`model_version: "${request.modelVersion}"`);
                  }

                  return lines.join("\n");
                };

                const requestDisplay = generateRequestDisplay();

                const handleCopy = () => {
                  navigator.clipboard.writeText(requestDisplay);
                  setRequestCopied(true);
                  setTimeout(() => setRequestCopied(false), 2000);
                };

                return (
                  <div className="space-y-4">
                    <h3 className="text-base font-semibold">Inference Request</h3>
                    <div className="relative group">
                      <pre className="text-xs bg-muted p-3 rounded overflow-x-auto font-mono">
                        <code>{requestDisplay}</code>
                      </pre>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={handleCopy}
                      >
                        <Icon name={requestCopied ? "check" : "copy"} className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })()}

            {audioDetails.reproducibilityInfo &&
              (() => {
                const repro = audioDetails.reproducibilityInfo;

                const generateCommand = () => {
                  const parts = ["uv run -m mirager.api.bin.client-audio"];

                  if (repro?.referenceAudioUrl) {
                    parts.push(`--reference-audio-uri "${repro.referenceAudioUrl}"`);
                  }

                  if (repro?.target) {
                    const escapedTarget = repro.target.replace(/"/g, '\\"');
                    parts.push(`--text "${escapedTarget}"`);
                  }

                  if (repro?.seed) {
                    parts.push(`--seed "${repro.seed}"`);
                  }

                  if (repro?.voiceDescription) {
                    const escapedDesc = repro.voiceDescription.replace(/"/g, '\\"');
                    parts.push(`--voice-description "${escapedDesc}"`);
                  }

                  return parts.join(" \\\n  ");
                };

                const command = generateCommand();

                const handleCopy = () => {
                  navigator.clipboard.writeText(command);
                  setYamlCopied(true);
                  setTimeout(() => setYamlCopied(false), 2000);
                };

                return (
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-base font-semibold mb-3">Reproduce Command</h3>
                    <div className="relative group">
                      <pre className="text-xs bg-muted p-3 rounded overflow-x-auto font-mono">
                        <code>{command}</code>
                      </pre>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={handleCopy}
                      >
                        <Icon name={yamlCopied ? "check" : "copy"} className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })()}

            {audioDetails.reproducibilityInfo?.serializedComputationGraphState &&
              (() => {
                let computationData;
                try {
                  computationData = JSON.parse(
                    audioDetails.reproducibilityInfo.serializedComputationGraphState
                  );
                } catch {
                  return null;
                }

                const stageMetadata: Record<string, ComputationStage> | undefined =
                  computationData?.stage_metadata;
                if (!stageMetadata) {
                  return null;
                }

                const stages = Object.entries(stageMetadata).map(([name, data]) => ({
                  name,
                  ...data,
                }));

                const handleCopyStage = (stageData: object) => {
                  navigator.clipboard.writeText(JSON.stringify(stageData, null, 2));
                };

                return (
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-base font-semibold mb-3">Detailed Computation Info</h3>
                    <Accordion type="single" collapsible className="w-full">
                      {stages.map((stage, index) => (
                        <AccordionItem key={stage.name} value={stage.name}>
                          <AccordionTrigger className="text-sm">
                            <div className="flex items-center gap-2 flex-1">
                              <span className="font-medium">
                                {index + 1}. {stage.stage_name || stage.name}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {stage.stage_latency_ms}ms
                              </span>
                              {stage.stage_result === "success" && (
                                <span className="text-xs text-green-600">✓</span>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3">
                              {stage.input && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-semibold text-muted-foreground">
                                      Input
                                    </h4>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-xs"
                                      onClick={() =>
                                        handleCopyStage({ stage: stage.name, input: stage.input })
                                      }
                                    >
                                      <Icon name="copy" className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto font-mono max-h-48 overflow-y-auto">
                                    <code>{JSON.stringify(stage.input, null, 2)}</code>
                                  </pre>
                                </div>
                              )}

                              {stage.output && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-semibold text-muted-foreground">
                                      Output
                                    </h4>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-xs"
                                      onClick={() =>
                                        handleCopyStage({ stage: stage.name, output: stage.output })
                                      }
                                    >
                                      <Icon name="copy" className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto font-mono max-h-48 overflow-y-auto">
                                    <code>{JSON.stringify(stage.output, null, 2)}</code>
                                  </pre>
                                </div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                );
              })()}
          </div>
        )}
      </div>
    </div>
  );
}

const ENVIRONMENT_TAGS = ["dev", "beta", "prod"];
const ALWAYS_AVAILABLE_TAGS = ["voice_design", "no_reference_transcription", "long_pause"];

function RouteComponent() {
  const {
    type,
    id,
    audioId: audioIdFromUrl,
    tags: selectedTags,
    userId: userIdFilter,
  } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [selectedAudioId, setSelectedAudioId] = useState<string | null>(audioIdFromUrl ?? null);
  const [jsonCopied, setJsonCopied] = useState(false);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    fetchMirageAudioQuery.useInfiniteQuery({
      params: {
        filters: {
          ...(userIdFilter ? { userId: userIdFilter } : {}),
          ...(!userIdFilter && id && type === "user" ? { userId: id } : {}),
          ...(selectedTags && selectedTags.length > 0 ? { tags: selectedTags } : {}),
        },
      },
    });

  const allAudio = useMemo(() => {
    return data?.pages.flatMap((page) => page.audio.page) ?? [];
  }, [data?.pages]);

  const allNonEnvironmentTags = useMemo(() => {
    const tagsSet = new Set<string>(ALWAYS_AVAILABLE_TAGS);
    allAudio.forEach((audio) => {
      audio.tags?.forEach((tag) => tagsSet.add(tag));
    });
    return Array.from(tagsSet)
      .filter((tag) => !ENVIRONMENT_TAGS.includes(tag.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
  }, [allAudio]);

  const selectedEnvironment = useMemo(() => {
    return selectedTags?.find((tag) => ENVIRONMENT_TAGS.includes(tag.toLowerCase())) ?? null;
  }, [selectedTags]);

  const selectedNonEnvironmentTags = useMemo(() => {
    return selectedTags?.filter((tag) => !ENVIRONMENT_TAGS.includes(tag.toLowerCase())) ?? [];
  }, [selectedTags]);

  const handleSelectAudio = (audioId: string) => {
    setSelectedAudioId(audioId);
    navigate({
      search: (prev) => ({ ...prev, audioId }),
    });
  };

  const handleClosePanel = () => {
    setSelectedAudioId(null);
    navigate({
      search: (prev) => {
        const { audioId: _audioId, ...rest } = prev;
        return rest;
      },
    });
  };

  const handleEnvironmentChange = (environment: string | null) => {
    const currentTags = selectedTags ?? [];
    const nonEnvTags = currentTags.filter((tag) => !ENVIRONMENT_TAGS.includes(tag.toLowerCase()));
    const newTags = environment ? [...nonEnvTags, environment] : nonEnvTags;

    navigate({
      search: (prev) => ({
        ...prev,
        tags: newTags.length > 0 ? newTags : undefined,
      }),
    });
  };

  const handleToggleTag = (tag: string) => {
    const currentTags = selectedTags ?? [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter((t) => t !== tag)
      : [...currentTags, tag];

    navigate({
      search: (prev) => ({
        ...prev,
        tags: newTags.length > 0 ? newTags : undefined,
      }),
    });
  };

  const handleCopyPageData = () => {
    const jsonData = JSON.stringify(allAudio, null, 2);
    navigator.clipboard.writeText(jsonData);
    setJsonCopied(true);
    setTimeout(() => setJsonCopied(false), 2000);
  };

  useEffect(() => {
    setSelectedAudioId(audioIdFromUrl ?? null);
  }, [audioIdFromUrl]);

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { rootMargin: "100px" }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const getTagColor = (tag: string) => {
    const tagColors = [
      "bg-blue-500/20 text-blue-700 border-blue-500/30",
      "bg-purple-500/20 text-purple-700 border-purple-500/30",
      "bg-pink-500/20 text-pink-700 border-pink-500/30",
      "bg-cyan-500/20 text-cyan-700 border-cyan-500/30",
      "bg-amber-500/20 text-amber-700 border-amber-500/30",
      "bg-teal-500/20 text-teal-700 border-teal-500/30",
    ];
    const hash = tag.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return tagColors[hash % tagColors.length];
  };

  return (
    <>
      <div className={`space-y-6 p-6 transition-all ${selectedAudioId ? "mr-[700px]" : ""}`}>
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Audio</h1>
          <Button variant="outline" size="sm" onClick={handleCopyPageData} className="gap-2">
            <Icon name={jsonCopied ? "check" : "copy"} className="h-4 w-4" />
            {jsonCopied ? "Copied!" : "Copy Page Data"}
          </Button>
        </div>

        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="filters" className="bg-slate-100/80 rounded-md px-3 border-0">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                <div className="flex items-center gap-2">
                  <span>Filters</span>
                  {(userIdFilter ||
                    selectedEnvironment ||
                    selectedNonEnvironmentTags.length > 0) && (
                    <span className="text-xs font-normal text-muted-foreground">
                      (
                      {
                        [
                          userIdFilter ? "User" : null,
                          selectedEnvironment,
                          ...selectedNonEnvironmentTags,
                        ].filter(Boolean).length
                      }{" "}
                      active)
                    </span>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  <div className="flex gap-4 items-end">
                    <div className="flex-1 max-w-md">
                      <h3 className="text-sm font-medium text-muted-foreground mb-2">User ID</h3>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter user ID..."
                          value={userIdFilter ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            navigate({
                              search: (prev) => ({
                                ...prev,
                                userId: value || undefined,
                              }),
                            });
                          }}
                          className="flex-1"
                        />
                        {userIdFilter && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              navigate({
                                search: (prev) => {
                                  const { userId: _userId, ...rest } = prev;
                                  return rest;
                                },
                              });
                            }}
                          >
                            <Icon name="cross-small" className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="w-48">
                      <h3 className="text-sm font-medium text-muted-foreground mb-2">
                        Environment
                      </h3>
                      <Select
                        value={selectedEnvironment ?? "all"}
                        onValueChange={(value) => {
                          handleEnvironmentChange(value === "all" ? null : value);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All environments" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All environments</SelectItem>
                          <SelectItem value="dev">Dev</SelectItem>
                          <SelectItem value="beta">Beta</SelectItem>
                          <SelectItem value="prod">Prod</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {allNonEnvironmentTags.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium text-muted-foreground">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {allNonEnvironmentTags.map((tag) => {
                          const isSelected = selectedNonEnvironmentTags?.includes(tag) ?? false;
                          return (
                            <button
                              key={tag}
                              onClick={() => handleToggleTag(tag)}
                              className={`px-3 py-1 text-xs rounded-full border font-medium transition-all ${
                                isSelected
                                  ? getTagColor(tag)
                                  : "bg-muted/50 text-muted-foreground border-muted hover:bg-muted"
                              }`}
                            >
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="max-w-3xl space-y-4">
          {isLoading &&
            Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-32 w-full" />
            ))}
          {allAudio.map((audio) => (
            <AudioFeedCard
              key={audio.id}
              audio={audio}
              isSelected={selectedAudioId === audio.id}
              onClick={() => handleSelectAudio(audio.id)}
            />
          ))}
          {!isLoading && allAudio.length === 0 && (
            <div className="flex text-sm text-muted-foreground">No audio found</div>
          )}
          {hasNextPage && <div ref={loadMoreRef} className="h-10" />}
          {isFetchingNextPage && (
            <div className="flex justify-center">
              <Skeleton className="h-32 w-full" />
            </div>
          )}
        </div>
      </div>

      {selectedAudioId && (
        <AudioDetailsPanel audioId={selectedAudioId} onClose={handleClosePanel} />
      )}
    </>
  );
}
